import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { FireSimple, Trophy, Crown, Heart, Gift, Play, Brain } from "@phosphor-icons/react";

function fmtCount(n) {
    if (n == null) return "0";
    if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
    if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
    return String(n);
}

function fmtDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days === 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function Trending() {
    const { t } = useTranslation();
    const nav = useNavigate();
    const [trending, setTrending] = useState([]);
    const [highlights, setHighlights] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/videos/trending?limit=24").then(({ data }) => setTrending(data || [])).catch(() => {}),
            api.get("/competitions/highlights?limit=8").then(({ data }) => setHighlights(data || [])).catch(() => {}),
        ]).finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="trending-page" className="max-w-6xl mx-auto px-4 py-6 space-y-10">
            {/* Header */}
            <header className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="flex items-center gap-2 text-wta-primary">
                        <FireSimple size={22} weight="fill" />
                        <span className="font-heading text-xs tracking-[0.3em] uppercase">Hot right now</span>
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mt-1">
                        Trending
                    </h1>
                    <p className="text-wta-muted text-sm mt-2 max-w-md">
                        The hottest videos of the last 2 weeks and headlines from recent tournaments.
                    </p>
                </div>
                <Link
                    to="/trivia/lobby"
                    data-testid="trending-trivia-cta"
                    className="btn-ghost rounded-sm px-4 py-2 text-xs font-heading uppercase tracking-wider flex items-center gap-2"
                >
                    <Brain size={14} /> Play Trivia
                </Link>
            </header>

            {/* Trending videos grid */}
            <section>
                <div className="flex items-end justify-between mb-4">
                    <h2 className="font-heading text-2xl uppercase tracking-wider">Going viral</h2>
                    <Link to="/feed" className="text-xs tracking-widest text-wta-muted uppercase hover:text-wta-primary">
                        See feed →
                    </Link>
                </div>
                {loading ? (
                    <div className="py-12 text-center text-wta-muted text-sm">{t("common.loading")}</div>
                ) : trending.length === 0 ? (
                    <EmptyState title="Nothing trending yet" subtitle="Be the first to upload a killer video." ctaTo="/upload" cta="Upload" />
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {trending.map((v, i) => (
                            <TrendingCard key={v.id} video={v} rank={i + 1} onOpen={() => nav(`/feed?v=${v.id}`)} />
                        ))}
                    </div>
                )}
            </section>

            {/* Tournament highlights */}
            <section>
                <div className="flex items-end justify-between mb-4">
                    <h2 className="font-heading text-2xl uppercase tracking-wider flex items-center gap-2">
                        <Trophy size={22} weight="fill" className="text-wta-accent" /> Tournament highlights
                    </h2>
                    <Link to="/competitions" className="text-xs tracking-widest text-wta-muted uppercase hover:text-wta-primary">
                        All tournaments →
                    </Link>
                </div>
                {loading ? (
                    <div className="py-12 text-center text-wta-muted text-sm">{t("common.loading")}</div>
                ) : highlights.length === 0 ? (
                    <EmptyState title="No finished tournaments yet" subtitle="Check back after the first champion is crowned." ctaTo="/competitions" cta="Browse competitions" />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {highlights.map((h) => <HighlightCard key={h.id} item={h} />)}
                    </div>
                )}
            </section>
        </div>
    );
}

function TrendingCard({ video, rank, onOpen }) {
    const thumb = video.thumbnail || (video.video_url && !/\.(mp4|webm|mov)/i.test(video.video_url) ? video.video_url : null);
    return (
        <button
            type="button"
            onClick={onOpen}
            data-testid={`trending-video-${rank}`}
            className="group relative aspect-[9/16] bg-wta-surface border border-wta-border rounded-sm overflow-hidden text-left"
        >
            {thumb ? (
                <img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-105" />
            ) : video.video_url ? (
                <video src={video.video_url} muted playsInline className="absolute inset-0 w-full h-full object-cover" />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center text-wta-muted">
                    <Play size={40} />
                </div>
            )}
            {/* Rank badge */}
            <div className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm border border-wta-border rounded-sm px-2 py-1">
                <span className="font-heading text-xs tracking-widest text-wta-accent">#{rank}</span>
            </div>
            {/* Gradient overlay for legibility */}
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/90 to-transparent pointer-events-none" />
            {/* Footer info */}
            <div className="absolute inset-x-0 bottom-0 p-2.5 text-white">
                <div className="text-xs font-heading tracking-wider truncate">@{video.user?.name || "creator"}</div>
                <div className="flex items-center gap-3 text-[11px] mt-1 text-white/90">
                    <span className="inline-flex items-center gap-0.5"><Heart size={11} weight="fill" className="text-wta-primary" /> {fmtCount(video.likes_count)}</span>
                    {video.gifts_value > 0 && (
                        <span className="inline-flex items-center gap-0.5"><Gift size={11} weight="fill" className="text-wta-accent" /> {fmtCount(video.gifts_value)}</span>
                    )}
                </div>
            </div>
        </button>
    );
}

function HighlightCard({ item }) {
    const cover = item.winner_thumbnail || item.cover_image || item.winner?.profile_pic;
    return (
        <Link
            to={`/competitions/${item.id}`}
            data-testid={`highlight-${item.id}`}
            className="group bg-wta-surface border border-wta-border rounded-sm overflow-hidden flex flex-col md:flex-row hover:border-wta-primary transition-colors"
        >
            <div className="relative md:w-56 aspect-video md:aspect-[4/5] shrink-0 bg-wta-surface2">
                {cover ? (
                    <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-wta-muted">
                        <Trophy size={48} />
                    </div>
                )}
                {item.winner_video && (
                    <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full w-9 h-9 flex items-center justify-center border border-wta-border">
                        <Play size={14} weight="fill" className="text-white ml-0.5" />
                    </div>
                )}
            </div>
            <div className="flex-1 p-4 flex flex-col">
                <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-wta-muted">
                    <span className="px-2 py-0.5 bg-wta-primary/15 text-wta-primary rounded-sm">
                        {item.category || "tournament"}
                    </span>
                    {item.tier && <span>{item.tier}</span>}
                    <span>· ended {fmtDate(item.ended_at)}</span>
                </div>
                <h3 className="font-heading text-xl tracking-wide mt-2 line-clamp-2">
                    {item.title || "Tournament"}
                </h3>
                {item.winner ? (
                    <div className="mt-auto pt-3 flex items-center gap-2">
                        <Crown size={16} weight="fill" className="text-wta-accent shrink-0" />
                        <span className="text-xs text-wta-muted">Champion:</span>
                        <span className="font-heading text-sm tracking-wide">@{item.winner.name}</span>
                        {item.prize_pool > 0 && (
                            <span className="ml-auto text-xs text-wta-accent font-heading">
                                {fmtCount(item.prize_pool)} coins
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="mt-auto pt-3 text-xs text-wta-muted">No winner declared</div>
                )}
            </div>
        </Link>
    );
}

function EmptyState({ title, subtitle, ctaTo, cta }) {
    return (
        <div className="border border-wta-border rounded-sm p-10 text-center bg-wta-surface">
            <div className="font-heading text-lg tracking-wider mb-1">{title}</div>
            <div className="text-sm text-wta-muted mb-4">{subtitle}</div>
            <Link to={ctaTo} className="btn-primary inline-block px-5 py-2 rounded-sm text-sm">{cta}</Link>
        </div>
    );
}
